// Phase HUB-DRILL-1 — Shared sections for the Main Hub drill-down (used
// both inside the slide-over drawer and on the full-page route).
//
// Renders: header, manager card, micro-hubs table (8 cols), staff
// breakdown (5 categories — leads/packers/pickup/delivery/ops_staff),
// recent-orders table (8 cols) with filters.
//
// Tracking-ID clicks open the unified OrderDetailDrawer via
// `useOrderDrawer()` — never a new screen.
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Building2, ChevronDown, ChevronRight, Phone, Mail, MapPin, Users,
  Loader2, RefreshCw, X, Filter, ExternalLink, BadgeCheck, Package,
} from "lucide-react";
import StatusBadge from "../hub/StatusBadge";
import { useOrderDrawer } from "../hub/OrderDrawerContext";

const ORANGE = "#FF6B00";

function StatusPill({ s }) {
  if (!s) return null;
  const map = {
    active:           "bg-emerald-100 text-emerald-700 border-emerald-200",
    inactive:         "bg-slate-100 text-slate-600 border-slate-200",
    pending_approval: "bg-amber-100 text-amber-700 border-amber-200",
    rejected:         "bg-rose-100 text-rose-700 border-rose-200",
  };
  const lbl = { active: "Active", inactive: "Inactive",
                 pending_approval: "Pending approval", rejected: "Rejected" }[s] || s;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${map[s] || map.inactive}`}
          data-status={s}>
      {lbl}
    </span>
  );
}

function Section({ title, count, defaultOpen = true, children, testid, action }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid={testid}>
      <button onClick={() => setOpen((v) => !v)}
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF6B00]">
        <div className="flex items-center gap-2">
          {open ? <ChevronDown className="h-4 w-4 text-slate-500"/> : <ChevronRight className="h-4 w-4 text-slate-500"/>}
          <span className="text-sm font-semibold text-slate-800">{title}</span>
          {typeof count === "number" && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-orange-100 text-[#C2410C] font-semibold">
              {count}
            </span>
          )}
        </div>
        {action}
      </button>
      {open && <div className="px-4 pb-4">{children}</div>}
    </section>
  );
}

function StaffGroup({ label, rows, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-slate-100 rounded-xl overflow-hidden" data-testid={testid}>
      <button onClick={() => setOpen((v) => !v)}
              className="w-full flex items-center justify-between px-3 py-2 bg-slate-50/60 hover:bg-slate-100 text-left">
        <div className="flex items-center gap-2 text-sm">
          {open ? <ChevronDown className="h-3.5 w-3.5"/> : <ChevronRight className="h-3.5 w-3.5"/>}
          <span className="font-medium">{label}</span>
          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-white border border-slate-200 text-slate-600">
            {rows.length}
          </span>
        </div>
      </button>
      {open && (
        rows.length === 0
          ? <div className="px-3 py-2 text-xs text-slate-400">No matches.</div>
          : <ul className="divide-y divide-slate-100">
              {rows.map((r) => (
                <li key={r.user_id} className="px-3 py-2 flex items-start gap-3 text-xs">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-slate-800 truncate">{r.name || r.email}</div>
                    <div className="text-slate-500 text-[11px] truncate inline-flex items-center gap-2">
                      {r.email && <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3"/>{r.email}</span>}
                      {r.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3"/>{r.phone}</span>}
                    </div>
                    {(r.capabilities || []).length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {r.capabilities.map((c) => (
                          <span key={c} className="text-[9px] px-1 py-0.5 rounded bg-orange-50 text-[#C2410C] border border-orange-100 uppercase tracking-wider">{c}</span>
                        ))}
                      </div>
                    )}
                  </div>
                  <StatusPill s={r.status}/>
                </li>
              ))}
            </ul>
      )}
    </div>
  );
}

// Tracking-ID link that opens the unified drawer
function TidLink({ tid }) {
  const { open } = useOrderDrawer();
  if (!tid) return <span className="text-slate-400">—</span>;
  return (
    <button
      onClick={() => open(tid)}
      data-testid={`tid-link-${tid}`}
      className="font-mono text-xs text-[#C2410C] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF6B00] rounded">
      {tid}
    </button>
  );
}

export default function MainHubDetailView({ data, hubId, reload,
                                              filters, onFiltersChange,
                                              variant = "drawer" }) {
  const h = data?.hub || {};
  const mgr = data?.manager;
  const micros = data?.micro_hubs || [];
  const breakdown = data?.staff_breakdown || {};
  const details = data?.staff_details || {};
  const orders = data?.recent_orders || [];

  // Order filter local state — controlled by parent for URL sync optional
  const f = filters || { orders_micro_hub_id: "", orders_status: "",
                          orders_has: "", orders_from: "", orders_to: "",
                          staff_approval_status: "" };
  const setF = (k, v) => onFiltersChange?.({ ...f, [k]: v });
  const clear = () => onFiltersChange?.({});
  const activeFilterCount = Object.values(f).filter((x) => x).length;

  return (
    <div className="space-y-3" data-testid="hub-detail-view">
      {/* Header */}
      <section className="bg-white border border-slate-200 rounded-2xl p-4" data-testid="hub-detail-header">
        <div className="flex items-start gap-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <Building2 className="h-5 w-5 text-[#FF6B00]"/>
              <h2 className="font-['Outfit'] text-xl font-bold text-slate-900 truncate" data-testid="hub-detail-name">
                {h.name || "Hub"}
              </h2>
              <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200"
                    data-testid="hub-detail-id">
                {h.hub_id}
              </span>
              <StatusPill s={h.status}/>
            </div>
            <div className="text-xs text-slate-500 mt-1 inline-flex items-center gap-1.5">
              <MapPin className="h-3 w-3"/> {h.city || "—"}{h.state ? `, ${h.state}` : ""}
            </div>
            {h.address && <div className="text-xs text-slate-600 mt-1" title={h.address}>{h.address}</div>}
          </div>
          {variant === "drawer" && hubId && (
            <Link to={`/dashboard/founder/main-hubs/${hubId}`}
                  data-testid="hub-detail-open-full-page"
                  className="shrink-0 inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-lg text-white"
                  style={{ backgroundColor: ORANGE }}>
              Open Full Page <ExternalLink className="h-3 w-3"/>
            </Link>
          )}
        </div>
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
          <Cell label="Capacity"      value={h.capacity || "—"}/>
          <Cell label="Approval"      value={h.approval_status || "—"}/>
          <Cell label="Active"        value={h.active ? "Yes" : "No"}/>
          <Cell label="Created"       value={(h.created_at || "—").toString().slice(0, 10)}/>
        </div>
      </section>

      {/* Manager */}
      <Section title="Manager" testid="hub-detail-manager-section">
        {mgr ? (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500">Name</div>
              <div className="font-medium" data-testid="hub-detail-manager-name">{mgr.name || "—"}</div>
            </div>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-wider text-slate-500">Phone</div>
              {mgr.phone
                ? <a href={`tel:${mgr.phone}`} className="text-[#C2410C] hover:underline inline-flex items-center gap-1"><Phone className="h-3 w-3"/>{mgr.phone}</a>
                : <span className="text-slate-400">—</span>}
            </div>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-wider text-slate-500">Email</div>
              <div className="text-slate-700 truncate inline-flex items-center gap-1"><Mail className="h-3 w-3"/>{mgr.email || "—"}</div>
            </div>
          </div>
        ) : <div className="text-sm text-slate-400 italic">Unassigned</div>}
      </Section>

      {/* Micro Hubs table */}
      <Section title="Micro hubs under this hub" count={micros.length}
               testid="hub-detail-micro-hubs-section">
        {micros.length === 0
          ? <div className="text-sm text-slate-400 italic">No micro hubs yet.</div>
          : (
            <div className="overflow-x-auto -mx-4">
              <table className="w-full text-xs min-w-[920px]" data-testid="hub-detail-micro-hubs-table">
                <thead className="bg-slate-50/70">
                  <tr className="text-left text-slate-500">
                    <Th>Micro hub</Th>
                    <Th>ID</Th>
                    <Th>Pincode</Th>
                    <Th>Lead</Th>
                    <Th>Approval</Th>
                    <Th>Active</Th>
                    <Th className="text-right">Orders today</Th>
                    <Th className="text-right">Pending</Th>
                    <Th className="text-right">Staff</Th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {micros.map((m) => (
                    <tr key={m.hub_id} data-testid={`hub-detail-micro-row-${m.hub_id}`}
                        className="hover:bg-orange-50/30">
                      <td className="px-3 py-2">
                        <Link to={`/dashboard/founder/micro-hubs/${m.hub_id}`}
                              className="text-[#C2410C] hover:underline font-medium">
                          {m.name || "—"}
                        </Link>
                      </td>
                      <td className="px-3 py-2 font-mono text-[10px] text-slate-600">{m.hub_id}</td>
                      <td className="px-3 py-2">{m.pincode || "—"}</td>
                      <td className="px-3 py-2">
                        <div className="font-medium">{m.lead_name || <span className="text-slate-400 italic">Unassigned</span>}</div>
                        <div className="text-[10px] text-slate-500 truncate">{m.lead_phone || ""} {m.lead_email ? ` · ${m.lead_email}` : ""}</div>
                      </td>
                      <td className="px-3 py-2"><span className="capitalize">{m.approval_status}</span></td>
                      <td className="px-3 py-2">{m.active ? "Yes" : "No"}</td>
                      <td className="px-3 py-2 text-right font-semibold text-[#FF6B00]">{m.orders_today}</td>
                      <td className="px-3 py-2 text-right">{m.pending_orders_count}</td>
                      <td className="px-3 py-2 text-right">{m.staff_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </Section>

      {/* Staff breakdown */}
      <Section title="Staff breakdown" testid="hub-detail-staff-section"
               action={
                 <select value={f.staff_approval_status || ""}
                         onChange={(e) => { e.stopPropagation(); setF("staff_approval_status", e.target.value); }}
                         onClick={(e) => e.stopPropagation()}
                         data-testid="hub-detail-staff-approval-filter"
                         className="text-xs border border-slate-200 rounded-lg px-2 py-1">
                   <option value="">All statuses</option>
                   <option value="active">Active</option>
                   <option value="inactive">Inactive</option>
                   <option value="pending_approval">Pending approval</option>
                 </select>
               }>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <StaffGroup testid="staff-group-leads"           label={`Leads · ${breakdown.leads_count ?? 0}`}                   rows={details.leads || []}/>
          <StaffGroup testid="staff-group-packers"         label={`Packers · ${breakdown.packers_count ?? 0}`}               rows={details.packers || []}/>
          <StaffGroup testid="staff-group-pickup_agents"   label={`Pickup agents · ${breakdown.pickup_agents_count ?? 0}`}    rows={details.pickup_agents || []}/>
          <StaffGroup testid="staff-group-delivery_agents" label={`Delivery agents · ${breakdown.delivery_agents_count ?? 0}`} rows={details.delivery_agents || []}/>
          <StaffGroup testid="staff-group-ops_staff"       label={`Ops staff · ${breakdown.ops_staff_count ?? 0}`}             rows={details.ops_staff || []}/>
        </div>
      </Section>

      {/* Recent orders */}
      <Section title="Recent orders" count={orders.length}
               testid="hub-detail-recent-orders-section"
               action={
                 <span className="text-[10px] text-slate-500 inline-flex items-center gap-1">
                   <Filter className="h-3 w-3"/>{activeFilterCount} filter{activeFilterCount === 1 ? "" : "s"}
                 </span>
               }>
        {/* Filters row */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-3" data-testid="hub-detail-orders-filters">
          <select value={f.orders_micro_hub_id || ""}
                  onChange={(e) => setF("orders_micro_hub_id", e.target.value)}
                  data-testid="orders-filter-micro-hub"
                  className="text-xs border border-slate-200 rounded-lg px-2 py-1.5">
            <option value="">All micro hubs</option>
            {micros.map((m) => <option key={m.hub_id} value={m.hub_id}>{m.name || m.hub_id}</option>)}
          </select>
          <select value={f.orders_status || ""}
                  onChange={(e) => setF("orders_status", e.target.value)}
                  data-testid="orders-filter-status"
                  className="text-xs border border-slate-200 rounded-lg px-2 py-1.5">
            <option value="">All statuses</option>
            {["pending","picked","packed","handed_over","received_at_main_hub","out_for_delivery","delivered","failed"].map((s) => (
              <option key={s} value={s}>{s.replace(/_/g, " ")}</option>
            ))}
          </select>
          <select value={f.orders_has || ""}
                  onChange={(e) => setF("orders_has", e.target.value)}
                  data-testid="orders-filter-has"
                  className="text-xs border border-slate-200 rounded-lg px-2 py-1.5">
            <option value="">All assignees</option>
            <option value="pickup">Has pickup agent</option>
            <option value="packer">Has packer</option>
            <option value="delivery">Has delivery agent</option>
          </select>
          <input type="date" value={f.orders_from || ""}
                 onChange={(e) => setF("orders_from", e.target.value)}
                 data-testid="orders-filter-from"
                 className="text-xs border border-slate-200 rounded-lg px-2 py-1.5"/>
          <input type="date" value={f.orders_to || ""}
                 onChange={(e) => setF("orders_to", e.target.value)}
                 data-testid="orders-filter-to"
                 className="text-xs border border-slate-200 rounded-lg px-2 py-1.5"/>
          {activeFilterCount > 0 && (
            <button onClick={clear} data-testid="orders-filter-clear"
                    className="col-span-2 sm:col-span-5 text-[11px] text-[#FF6B00] hover:underline inline-flex items-center gap-1 self-start mt-0.5">
              <X className="h-3 w-3"/> Clear all filters
            </button>
          )}
        </div>
        {orders.length === 0
          ? <div className="text-center text-sm text-slate-400 py-6" data-testid="hub-detail-orders-empty">
              No matches. {activeFilterCount > 0 && <button onClick={clear} className="text-[#FF6B00] hover:underline ml-1">Clear all</button>}
            </div>
          : (
            <div className="overflow-x-auto -mx-4">
              <table className="w-full text-xs min-w-[1000px]" data-testid="hub-detail-recent-orders-table">
                <thead className="bg-slate-50/70">
                  <tr className="text-left text-slate-500">
                    <Th>Tracking ID</Th>
                    <Th>Customer</Th>
                    <Th>Micro Hub</Th>
                    <Th>Status</Th>
                    <Th>Pickup</Th>
                    <Th>Packer</Th>
                    <Th>Delivery</Th>
                    <Th>Payment</Th>
                    <Th>Timeline</Th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {orders.map((o) => (
                    <tr key={o.tracking_id} data-testid={`hub-detail-order-row-${o.tracking_id}`}
                        className="hover:bg-orange-50/30">
                      <td className="px-3 py-2"><TidLink tid={o.tracking_id}/></td>
                      <td className="px-3 py-2">{o.customer_name || "—"}</td>
                      <td className="px-3 py-2 font-mono text-[10px] text-slate-600">{o.micro_hub_id}</td>
                      <td className="px-3 py-2"><StatusBadge status={o.fulfillment_status} size="sm"/></td>
                      <td className="px-3 py-2 text-slate-700">{o.pickup_agent_name || <span className="text-slate-400">—</span>}</td>
                      <td className="px-3 py-2 text-slate-700">{o.packer_name || <span className="text-slate-400">—</span>}</td>
                      <td className="px-3 py-2 text-slate-700">{o.delivery_agent_name || <span className="text-slate-400">—</span>}</td>
                      <td className="px-3 py-2 text-slate-700 whitespace-nowrap">{o.payment_status || "—"}</td>
                      <td className="px-3 py-2 text-[10px] text-slate-500">{o.timeline_status_short || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="text-center mt-3">
                <Link to={`/dashboard/orders?main_hub_id=${hubId || h.hub_id || ""}`}
                      className="text-xs text-[#FF6B00] hover:underline">
                  View all orders →
                </Link>
              </div>
            </div>
          )}
      </Section>
    </div>
  );
}

function Th({ children, className = "" }) {
  return <th className={`px-3 py-2 font-semibold uppercase tracking-wider text-[10px] ${className}`}>{children}</th>;
}

function Cell({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-slate-800 font-medium">{value}</div>
    </div>
  );
}

// Error card & loading shimmer — exported for reuse from the drawer & full page
export function HubDetailLoading() {
  return (
    <div className="space-y-3" data-testid="hub-detail-loading">
      {[0, 1, 2].map((i) => (
        <div key={i} className="bg-white border border-slate-200 rounded-2xl p-4">
          <div className="h-4 w-32 bg-slate-200 rounded animate-pulse mb-3"/>
          <div className="h-3 w-full bg-slate-100 rounded animate-pulse mb-2"/>
          <div className="h-3 w-2/3 bg-slate-100 rounded animate-pulse"/>
        </div>
      ))}
    </div>
  );
}

export function HubDetailError({ message, onRetry, code }) {
  return (
    <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6 text-center"
         data-testid="hub-detail-error">
      <div className="text-rose-700 font-semibold mb-1">Couldn't load</div>
      <div className="text-xs text-rose-600 mb-3 max-w-md mx-auto">{message}</div>
      <div className="flex justify-center gap-2">
        {onRetry && (
          <button onClick={onRetry} data-testid="hub-detail-retry"
                  className="text-xs font-semibold inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-white"
                  style={{ backgroundColor: ORANGE }}>
            <RefreshCw className="h-3 w-3"/> Retry
          </button>
        )}
        <Link to="/dashboard/ceo" data-testid="hub-detail-back"
              className="text-xs inline-flex items-center px-3 py-1.5 rounded-lg border border-slate-300 text-slate-700">
          Back to Central Operations
        </Link>
      </div>
      {code && <div className="text-[10px] text-rose-400 mt-2">code: {code}</div>}
    </div>
  );
}
